import React from 'react';
import { useTranslation } from 'react-i18next';
import { legalDocumentPathForUiLocale, normalizeUiLocale } from '../../legal/documents';

interface TermsAcceptanceProps {
  /**
   * Unique prefix for the checkbox id/label/error ids so two forms rendered on
   * the same page keep distinct, associated controls.
   */
  idPrefix: string;
  /** Whether the Terms checkbox is currently accepted. Always starts unchecked. */
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Localized message key for a missing/invalid acceptance, if any. */
  error?: string | null;
  /**
   * True after the server reported the accepted document version is stale
   * (409 terms_version_stale). The notice asks the visitor to re-read the
   * updated Terms; it never accepts the new version on their behalf.
   */
  stale?: boolean;
  className?: string;
}

/**
 * The single Terms acceptance control shared by registration and anonymous
 * quote submission. Renders the required checkbox (initially unchecked) with
 * the Terms link and localized error / stale notices. The checkbox is never pre-checked and never inferred from an
 * unseen footer.
 */
export const TermsAcceptance: React.FC<TermsAcceptanceProps> = ({
  idPrefix,
  checked,
  onChange,
  error,
  stale = false,
  className,
}) => {
  const { t, i18n } = useTranslation();
  const uiLocale = normalizeUiLocale(i18n?.resolvedLanguage || i18n?.language);

  const checkboxId = `${idPrefix}-terms-accepted`;
  const errorId = `${idPrefix}-terms-error`;
  const staleId = `${idPrefix}-terms-stale`;
  const privacyHref = legalDocumentPathForUiLocale('privacy', uiLocale);

  const describedBy =
    [error ? errorId : null, stale ? staleId : null].filter(Boolean).join(' ') || undefined;

  const linkClass = 'font-medium text-foreground underline underline-offset-2 hover:no-underline';

  return (
    <div className={className}>
      <div className="flex items-start gap-2">
        <input
          id={checkboxId}
          name="terms_accepted"
          type="checkbox"
          checked={checked}
          onChange={onChange}
          required
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className="mt-0.5 h-4 w-4 rounded border-input text-foreground focus:ring-ring"
        />
        <label htmlFor={checkboxId} className="text-sm leading-snug">
          {t('legal.terms_acceptance.label_before')}
          <a
            href={legalDocumentPathForUiLocale('terms', uiLocale)}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            {t('legal.terms')}
          </a>
          {t('legal.terms_acceptance.label_after')}
        </label>
      </div>

      {error && (
        <p id={errorId} role="alert" className="mt-2 ml-7 text-sm text-destructive">
          {t(error)}
        </p>
      )}

      {stale && (
        <p id={staleId} role="alert" className="mt-2 ml-7 text-sm text-destructive">
          {t('legal.terms_acceptance.stale_notice')}
        </p>
      )}
      <p className="mt-1 ml-6 text-xs text-muted-foreground">
        {t('legal.terms_acceptance.privacy_notice_before')}
        <a href={privacyHref} target="_blank" rel="noopener noreferrer" className={linkClass}>{t('legal.privacy')}</a>
        {t('legal.terms_acceptance.privacy_notice_after')}
      </p>
    </div>
  );
};
